import React, { useEffect, useRef } from 'react';
import { HashtagIcon } from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import MessageBubble from './shared/MessageBubble';
import { groupMessagesByDay, MessageDateSeparator } from './shared/MessageDateGroup';

/**
 * Channel message display area
 * @param {Array} messages - Array of message objects
 * @param {number} currentUserId - Current user's ID
 * @param {boolean} loading - Whether messages are loading
 */
const ChannelMessageArea = ({ messages = [], currentUserId, loading }) => {
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    if (messages.length > 0) {
      setTimeout(() => scrollToBottom(), 100);
    }
  }, [messages]);

  // Format messages for display
  const formatMessage = (msg) => {
    const userName = msg.staff_member || msg.username || msg.op || 'Unknown';
    const userInitials = userName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || 'U';
    
    return {
      id: msg.id,
      userId: msg.user_id,
      userName: userName,
      userInitials: userInitials,
      message: msg.message,
      timestamp: new Date(msg.timestamp || msg.created_at),
      op: msg.op,
      staff_member: msg.staff_member,
      username: msg.username
    };
  };

  const formattedMessages = messages.map(formatMessage);
  const groupedMessages = groupMessagesByDay(formattedMessages);

  if (loading && messages.length === 0) {
    return (
      <div className="flex-1 overflow-y-auto px-8 py-6 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
          <p className="text-gray-500">Loading messages...</p>
        </div>
      </div>
    );
  }

  if (groupedMessages.length === 0) {
    return (
      <div className="flex-1 overflow-y-auto px-8 py-6 flex items-center justify-center">
        <div className="text-center">
          <HashtagIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">No messages yet. Start the conversation!</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto px-8 py-6 space-y-4">
      {groupedMessages.map(({ dateDisplay, messages: dayMessages }) => (
        <div key={dateDisplay} className="mb-6">
          <MessageDateSeparator dateDisplay={dateDisplay} />
          {dayMessages.map((message, index) => {
            const showAvatar = index === 0 || 
              dayMessages[index - 1].userId !== message.userId ||
              (message.timestamp - dayMessages[index - 1].timestamp) > 5 * 60 * 1000; // 5 minutes

            return (
              <div key={message.id} className="flex space-x-3 group hover:bg-gray-50 px-2 py-1 -mx-2 rounded-md">
                {showAvatar ? (
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 rounded-full bg-indigo-600 flex items-center justify-center text-white font-semibold text-sm">
                      {message.userInitials}
                    </div>
                  </div>
                ) : (
                  <div className="w-10 flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  {showAvatar && (
                    <div className="flex items-baseline space-x-2 mb-1">
                      <span className="text-sm font-semibold text-gray-900">
                        {message.userName}
                      </span>
                      <span className="text-xs text-gray-500">
                        {format(message.timestamp, 'HH:mm')}
                      </span>
                    </div>
                  )}
                  <div className="text-sm text-gray-700 whitespace-pre-wrap break-words">
                    {message.message}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ))}
      <div ref={messagesEndRef} />
    </div>
  );
};

export default ChannelMessageArea;

